/**
 * PATH: packages/email-hub/src/feature/personal/PersonalEmailRow.tsx
 * FILE_ID: EMAILHUB-PERSONAL-ROW-001
 * PURPOSE: ONE cross-org Personal inbox row — the universal <EmailRow> body
 *          stamped with its SOURCE-ORG pill + a cross-tag "Add to board"
 *          affordance, deliberately WITHOUT the org-bound triage tooling.
 * WHY:     FEAT-2026-06-22-CROSS-ORG-PERSONAL. The aggregate inbox spans many
 *          orgs, so the rich FolderEmailRow (whose star / bin / lane chip all
 *          bind to the ONE active org) would mis-target a foreign-org row.
 *          This row is read + cross-tag only, which is exactly what the
 *          backend exposes for Personal emails (no per-email mutation, only
 *          private pins) — so it can never write into the wrong org.
 * FLOW:
 *   PersonalEmail → getCategoryConfig (pure) → <EmailRow> + source pill +
 *   "Add to board" (→ onAddToBoard, opens the cross-org picker)
 * DEPENDENCIES:
 *   - @quotech/ui EmailRow + EmailMessage: the universal presentational row.
 *   - lib/categoryConfig.getCategoryConfig + hooks/useEmailLabelPrefs: the
 *     SAME pure category resolver the Inbox uses (themed, no side effects).
 *   - PersonalSourcePill: the per-row org badge.
 * RELATED:
 *   - PersonalInboxPage.tsx: owns the list + the single AddToBoardDialog.
 *   - components/FolderEmailRow.tsx: the single-org rich row this intentionally
 *     is NOT (it omits the active-org allocation chip / mutations).
 */
import type { MouseEvent } from 'react'
import { Plus } from 'lucide-react'
import { EmailRow, type EmailMessage } from '@quotech/ui'

import { cn } from '../../_internal/lib/utils'
import { useOrgCustomCategories, usePersonalCategories } from '../hooks/useEmailLabelPrefs'
import { getCategoryConfig } from '../lib/categoryConfig'
import { ReferToOrgAction } from '../referrals/ReferToOrgAction'
import { SuggestedOrgChip } from '../referrals/SuggestedOrgChip'
import { PersonalSourcePill } from './PersonalSourcePill'
import type { PersonalEmail } from './usePersonalData'

interface PersonalEmailRowProps {
  email: PersonalEmail
  isSelected: boolean
  onSelect: () => void
  onAddToBoard: (email: PersonalEmail) => void
  onOpenOrgCopy: (copy: PersonalEmail['present_in_orgs'][number]) => void
  /** Count of the caller's pins for this email — drives the "pinned" hint. */
  pinnedCount?: number
}

export function PersonalEmailRow({
  email,
  isSelected,
  onSelect,
  onAddToBoard,
  onOpenOrgCopy,
  pinnedCount = 0,
}: PersonalEmailRowProps) {
  const personalCategories = usePersonalCategories()
  const orgCustomCategories = useOrgCustomCategories()
  const customCategories = [...personalCategories, ...orgCustomCategories]
  const catKey = email.category || 'uncategorized'
  const catCfg = getCategoryConfig(catKey, customCategories)
  const dotIsMuted = catKey === 'general' || catKey === 'uncategorized'
  const organisationCopies = email.present_in_orgs.length > 0
    ? email.present_in_orgs
    : [{
        org_id: email.source_org_id,
        org_name: email.source_org_name,
        email_id: email.id,
      }]
  const visibleCopies = organisationCopies.slice(0, 3)
  const hiddenCopies = organisationCopies.slice(3)
  const canOpenOrgCopy = new Set(
    organisationCopies.map((copy) => copy.org_id),
  ).size > 1

  const handleAdd = (e: MouseEvent<HTMLButtonElement>) => {
    e.preventDefault()
    e.stopPropagation()
    onAddToBoard(email)
  }

  return (
    <EmailRow
      email={email as unknown as EmailMessage}
      category={{
        icon: catCfg.icon,
        dot: catCfg.dot,
        color: catCfg.hex,
        label: catCfg.label,
        dotIsMuted,
      }}
      isSelected={isSelected}
      isChecked={false}
      // No bulk selection / no read-state mutation cross-org — the Personal
      // lens is read + cross-tag only, so we hide the dead checkbox rather
      // than show a click-does-nothing affordance.
      selectable={false}
      onSelect={onSelect}
      onToggleSelect={() => {}}
      // Star is shown as state (read-only); cross-org write isn't wired in v1.
      onToggleStar={() => {}}
      // The cross-tag entry point lives in the "Add to board" slot.
      allocateChip={
        <span className="inline-flex items-center gap-1">
          <button
            type="button"
            onClick={handleAdd}
            title="Add this email to a board (any organisation you belong to)"
            className={cn(
              'inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[11px] font-medium',
              pinnedCount > 0
                ? 'border-emerald-200 bg-emerald-50 text-emerald-700'
                : 'border-slate-200 bg-white text-slate-600 hover:border-slate-300 hover:bg-slate-50',
            )}
          >
            <Plus className="h-3 w-3" />
            {pinnedCount > 0 ? `On ${pinnedCount} board${pinnedCount > 1 ? 's' : ''}` : 'Add to board'}
          </button>
          <SuggestedOrgChip emailId={email.id} suggested={email.suggested_org} />
          <ReferToOrgAction emailId={email.id} />
        </span>
      }
      mailboxChip={
        <span className="inline-flex items-center gap-1">
          {visibleCopies.map((copy) => (
            <PersonalSourcePill
              key={`${copy.org_id}:${copy.email_id}`}
              orgId={copy.org_id}
              orgName={copy.org_name}
              onOpen={
                canOpenOrgCopy
                  ? () => onOpenOrgCopy(copy)
                  : undefined
              }
            />
          ))}
          {hiddenCopies.length > 0 && (
            <span
              title={hiddenCopies.map((copy) => copy.org_name).join(', ')}
              className="text-[11px] font-medium text-slate-500"
            >
              +{hiddenCopies.length}
            </span>
          )}
        </span>
      }
    />
  )
}
